import { until } from '@vueuse/core';
import { JsonApiDocument, JsonApiIdentifier, JsonApiResource } from 'json-api-models';
import {
    computed,
    ComputedRef,
    MaybeRefOrGetter,
    nextTick,
    Ref,
    ref,
    ShallowRef,
    shallowRef,
    toRef,
    watch,
} from 'vue';

import { useApiRequest, UseApiRequestReturn } from './api.ts';

export interface UseResourceListReturn<T = any> {
    url: Ref<string>;
    request: UseApiRequestReturn<T[]> & PromiseLike<UseApiRequestReturn<T[]>>;
    document: ComputedRef<JsonApiDocument | null>;
    models: ShallowRef<T[]>;
    hasMore: ComputedRef<boolean>;
    allLoaded: Ref<boolean>;
    loadMore: () => Promise<void>;
    loadAll: () => Promise<void>;
    isLoading: Ref<boolean>;
    /** Whether the list has yet to finish its first load. */
    isPending: ComputedRef<boolean>;
    retry: () => Promise<void>;
    reload: () => Promise<void>;
    remove: (model: T) => void;
    destroy: () => void;
    update: (url: string) => void;
    invalidate: () => void;
    clear: () => void;
}

export interface UseResourceListOptions {
    keepPreviousData?: boolean;
    lazy?: boolean;
    /** Stop loading pages once this many models have loaded. */
    limit?: number;
}

export function useResourceList<T extends JsonApiResource = JsonApiResource>(
    url?: MaybeRefOrGetter<string>,
    options: UseResourceListOptions = {},
): UseResourceListReturn<T> {
    const initialUrl: Ref<string> = toRef(url || '');
    const requestUrl = ref(initialUrl.value);
    const request = useApiRequest<T[]>(requestUrl, { immediate: false });
    const document = computed(() => request.data.value);
    const models = shallowRef<T[]>([]);
    let pagesLoaded = 1;
    const started = ref(!options.lazy);
    let loadVersion = 0;
    const loadingCount = ref(0);
    const refreshingCount = ref(0);
    // A background refresh only counts as loading while there's nothing to
    // show yet. A list that loaded empty stays empty rather than flashing a
    // loading state each time it refreshes, and items kept after a failed
    // page, which clears the document, stay visible.
    const isLoading = computed(
        () =>
            !!loadingCount.value ||
            (!!refreshingCount.value && !document.value && !models.value.length),
    );
    const isPending = computed(() => !document.value && !request.isFinished.value);
    // A failed page clears the document, so a lazy list tracks whether it has
    // started rather than treating a missing document as not yet loaded.
    const hasMore = computed(
        () =>
            !started.value ||
            (!!document.value?.links?.next &&
                !(options.limit && models.value.length >= options.limit)),
    );

    // Completion belongs to the displayed items, which stay visible while
    // their pages are refreshed in the background.
    const allLoaded = ref(false);

    async function loadMore() {
        if (isLoading.value || request.isFetching.value) return;
        if (!started.value) {
            return reload();
        }
        const next = hasMore.value && document.value?.links?.next;
        if (!next) return;
        requestUrl.value = next;
        const version = ++loadVersion;
        pagesLoaded++;
        loadingCount.value++;
        await request.execute();
        loadingCount.value--;
        if (version !== loadVersion) return;
        models.value = [...models.value, ...(request.model.value || [])].slice(0, options.limit);
        allLoaded.value = !!request.response.value?.ok && !request.error.value && !hasMore.value;
    }

    async function loadAll() {
        while (initialUrl.value) {
            await until(() => isLoading.value || request.isFetching.value).toBe(false);
            if (!hasMore.value || request.error.value) return;

            await loadMore();
        }
    }

    async function load(background: boolean) {
        const version = ++loadVersion;
        const count = background ? refreshingCount : loadingCount;
        count.value++;
        try {
            // Defer request reads so callers can reload inside watchEffect
            // without tracking the list's own state.
            await nextTick();
            started.value = true;
            if (!initialUrl.value || version !== loadVersion) return;
            requestUrl.value = initialUrl.value;
            await request.execute();
            // A replacement request aborts this one. Only the latest load may
            // replace the displayed items or continue fetching their pages.
            if (version !== loadVersion) return;
            const newModels = [...(request.model.value || [])];
            for (let i = 1; i < pagesLoaded; i++) {
                if (!document.value?.links?.next) break;
                requestUrl.value = document.value.links.next;
                await request.execute();
                if (version !== loadVersion) return;
                newModels.push(...(request.model.value || []));
            }
            models.value = newModels.slice(0, options.limit);
            allLoaded.value =
                !!request.response.value?.ok && !request.error.value && !hasMore.value;
        } finally {
            count.value--;
        }
    }

    function update(url: string) {
        started.value = true;
        if (initialUrl.value !== url) {
            initialUrl.value = url;
        } else {
            invalidate();
        }
    }

    function invalidate() {
        void load(true);
    }

    function retry() {
        return load(false);
    }

    function reload() {
        if (!options.keepPreviousData) {
            clear();
        }

        request.data.value = null;
        pagesLoaded = 1;
        return retry();
    }

    const destroy = watch(
        initialUrl,
        () => {
            if (started.value) void reload();
        },
        { immediate: !options.lazy && !!initialUrl.value, flush: 'sync' },
    );

    function remove(model: JsonApiIdentifier) {
        models.value = models.value.filter((m) => m.type !== model.type || m.id !== model.id);
    }

    function clear() {
        models.value = [];
        allLoaded.value = false;
        request.data.value = null;
    }

    return {
        url: initialUrl,
        update,
        request,
        document,
        models,
        hasMore,
        allLoaded,
        loadMore,
        loadAll,
        isLoading,
        isPending,
        retry,
        reload,
        remove,
        destroy,
        invalidate,
        clear,
    };
}
